import { Box, Stack } from "@mui/material";
import {
    EDIcon,
    EDIconSize,
    EDPlayerAvatar,
    EDTypography,
    error,
    grey,
    primary,
    success,
    spacing
} from "@trailblazer-game/ed-webkit";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { truncateString } from "../../../../helpers/utils/string";
import { ILeagueNFTPrize } from "../../../../helpers/interfaces/league";
import { formatCurrency } from "../../../../helpers/numbers";
import { theme } from "../../../Theme/Theme";

//
function getRankIcon(rank: number) {
    switch (rank) {
        case 1:
            return "/league/Icon=Gold.png";
        case 2:
            return "/league/Icon=Silver.png";
        case 3:
            return "/league/Icon=Bronze.png";
        default:
            return "";
    }
}

//
export const rankColumnDef = (tbUid: string | null) => {
    return {
        field: 'rank', headerName: '#', width: 40, sortable: false, renderCell: (params: any) => {
            const value = params.row.rank;
            const isTop3 = value <= 3;
            const isPlayerRow = params.row.tbUid === tbUid;
            return (
                <EDTypography
                    align={"center"}
                    sx={{ color: isTop3 ? primary("main") : isPlayerRow ? grey("1000") : '' }}
                    variant={isTop3 ? "h3" : "h6"}>
                    {value}
                </EDTypography>
            )
        }
    }
}

//
export const deltaColumnDef = () => {
    return {
        field: 'delta', headerName: 'Last #', width: 80, sortable: false,
        renderCell: (params: any) => {
            const value = params.row.delta || 0;
            const absValue = Math.abs(value);
            const icon = value == 0 ? <RemoveIcon /> : value > 0 ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />;
            const color = value == 0 ? grey("600") : value > 0 ? success("main") : error("main");
            return (
                <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
                    <EDIcon icon={icon} sx={{ color }} size={EDIconSize.Medium} />
                    <EDTypography
                        align={"center"}
                        variant={"h6"}
                        sx={{ color }}>
                        {absValue > 0 ? absValue : null}
                    </EDTypography>
                </Stack>
            )
        }
    }
}

//
export const nicknameColumnDef = (tbUid: string) => {
    return {
        field: 'nickname', headerName: 'Player', minWidth: 400, flex: 1, sortable: false,
        renderCell: (params: any) => {
            const isPlayerRow = params.row.tbUid == tbUid;
            return (
                <a href={`/u/${params.row.nickname}`} rel="noreferrer noopener"
                    style={{ position: "relative" }}>
                    {params.row.rank <= 3 && <EDIcon icon={getRankIcon(params.row.rank)} className={"winner-icon"}
                        size={EDIconSize.Normal} />}
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                        <EDPlayerAvatar avatarUrl={`${process.env.NEXT_PUBLIC_CDN_URL}/avatar/${params.id}`}
                            title={"Visit profile page"} />
                        <EDTypography variant={"body2"} title={params.row.nickname}
                            sx={{ color: isPlayerRow ? grey("1000") : primary("main") }}>
                            <b>{truncateString(params.row.nickname || "", 40)}</b>
                        </EDTypography>
                    </Stack>
                </a>
            );
        },
    }
}

//
export const winsColumnDef = (highlight: boolean) => {
    return {
        field: 'wins',
        headerName: 'Wins',
        flex: 0,
        minWidth: 75,
        sortable: false,
        renderCell: (params: any) => {
            const { wins } = params.row;

            return <Stack alignItems={"center"} justifyContent={"center"}
                sx={{
                    backgroundColor: highlight ? grey("1000") : '',
                    padding: theme.spacing(1),
                    flexGrow: 1,
                    borderRadius: spacing(0.25),
                    maxWidth: "50px"
                }}>

                <EDTypography align={"center"} variant={wins > 999 ? "caption" : "body2"} sx={{ color: highlight ? success("main") : '' }}>{wins}</EDTypography>
            </Stack>
        }
    }
}

//
export const ratioColumnDef = () => {
    return {
        field: 'ratio', headerName: 'Win Rate', flex: 0, minWidth: 75, sortable: false,
        renderCell: (params: any) => {
            return (
                <EDTypography
                    variant={"body2"}>{`${Math.floor(params.row[params.colDef.field] * 100)}%` || "0%"}</EDTypography>
            )
        }
    }
}

//
export const ratingColumnDef = () => {
    return {
        field: 'rating',
        headerName: 'Rating',
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
            return (
                <EDTypography variant={"body2"}>{params.row[params.colDef.field] || "-"}</EDTypography>
            );
        }
    }
}

//
export const expectedEarningsColumnDef = () => {
    return {
        field: 'earningExpected',
        headerName: 'Expected Rewards',
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderCell: (params: any) => {
            const { expectedEarnings } = params.row;

            if (!expectedEarnings || expectedEarnings.length == 0) {
                return "--";
            }

            const result: React.ReactNode[] = [];

            expectedEarnings!.forEach((earning: Partial<ILeagueNFTPrize>, index: number) => {
                const label = `${earning.name} x${formatCurrency(earning.amount || 0)}`;
                if ((earning.amount || 0) > 0) {
                    result.push(<Stack direction={"row"} alignItems={"center"} justifyContent={"flex-start"}
                        key={`earning-${index}`} title={label} spacing={0.5}>
                        {earning.image &&
                            <img src={earning.image} alt={"NFT Image"} style={{ width: '24px', height: '24px' }} />}
                        <EDTypography variant={"caption"}>
                            {label}
                        </EDTypography>
                    </Stack>);
                }
            })

            return (
                <Stack direction={"column"} alignItems={"flex-start"} justifyContent={"center"}>
                    {result}
                </Stack>
            )
        }
    }
}

//
export const guildNameColumnDef = () => {
    return {
        field: 'name', headerName: 'Guild Name', minWidth: 400, flex: 1, sortable: false,
        renderCell: (params: any) => {
            return (
                <Box
                    style={{ position: "relative" }}>
                    {params.row.rank <= 3 && <EDIcon icon={getRankIcon(params.row.rank)} className={"winner-icon"}
                        size={EDIconSize.Normal} />}
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                        <EDPlayerAvatar avatarUrl={`${process.env.NEXT_PUBLIC_CDN_URL}/guild/${params.id}`}
                            title={"Visit profile page"} />
                        <EDTypography variant={"body2"} title={params.row.name}
                            sx={{ color: primary("main") }}>
                            <b>{truncateString(params.row.name || "", 40)}</b>
                        </EDTypography>
                    </Stack>
                </Box>
            );
        },
    }
}

//
export const trophiesColumnDef = () => {
    return {
        field: 'trophies',
        headerName: 'Trophies',
        flex: 0,
        minWidth: 75,
        sortable: false,
        renderCell: (params: any) => {
            const { trophies } = params.row || 0;

            return <Stack alignItems={"center"} justifyContent={"space-between"} direction={"row"}
                sx={{
                    backgroundColor: grey("1000"),
                    padding: theme.spacing(1),
                    flexGrow: 1,
                    borderRadius: spacing(0.25),
                    maxWidth: "100px"
                }}>

                <EDIcon icon={'/league/Trophy_128x128.webp'} size={EDIconSize.Normal} />
                <EDTypography align={"center"} variant={trophies > 999 ? "caption" : "body2"} sx={{ color: success("main") }}>{trophies}</EDTypography>
            </Stack>
        }
    }
}
